import React, { useState, useCallback, useRef, useEffect } from 'react'
import { DashboardEditableRowSlotProvider } from './DashboardEditableRowSlotContext'
import './DashboardEditableRow.style.css'

type DashboardEditableRowProps = {
  children: React.ReactNode
  editMode?: boolean
  className?: string
  /** When not in edit mode, fix the last cell to this flex basis (e.g. '400px') */
  lastCellFlexBasis?: string
}

function DashboardEditableRow({
  children,
  editMode = false,
  className = '',
  lastCellFlexBasis,
}: DashboardEditableRowProps): React.ReactElement {
  const childArray = React.Children.toArray(children)
  const count = childArray.length
  /** Reorder / resize only apply between siblings; single-card rows have nothing to drag or split. */
  const multiSectionLayout = count > 1

  const [order, setOrder] = useState<number[]>(() =>
    Array.from({ length: count }, (_, i) => i),
  )
  const [flexBasisPct, setFlexBasisPct] = useState<(number | null)[]>(() =>
    Array(count).fill(null),
  )
  const rowRef = useRef<HTMLDivElement>(null)
  const resizeState = useRef<{
    index: number
    startX: number
    startWidths: [number, number]
  } | null>(null)
  const [resizing, setResizing] = useState<number | null>(null)

  useEffect(() => {
    if (count !== order.length) {
      setOrder(Array.from({ length: count }, (_, i) => i))
      setFlexBasisPct(Array(count).fill(null))
    }
  }, [count, order.length])

  const orderedChildren = order.map((i) => childArray[i])

  const handleDragStart = useCallback(
    (e: React.DragEvent, orderIndex: number) => {
      if (!editMode) return
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(orderIndex))
      e.dataTransfer.setData(
        'application/dashboard-slot-order',
        String(orderIndex),
      )
      ;(e.target as HTMLElement).classList.add(
        'dashboard-editable-row-dragging',
      )
    },
    [editMode],
  )

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    ;(e.target as HTMLElement).classList.remove(
      'dashboard-editable-row-dragging',
    )
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent, dropOrderIndex: number) => {
      if (!editMode) return
      e.preventDefault()
      const dragOrderIndex = parseInt(
        e.dataTransfer.getData('application/dashboard-slot-order') ?? '',
        10,
      )
      if (Number.isNaN(dragOrderIndex) || dragOrderIndex === dropOrderIndex)
        return
      setOrder((prev) => {
        const arr = [...prev]
        const [removed] = arr.splice(dragOrderIndex, 1)
        arr.splice(dropOrderIndex, 0, removed)
        return arr
      })
    },
    [editMode],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!editMode) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    },
    [editMode],
  )

  /**
   * leftIndex = order-index of the LEFT card in the pair being resized.
   * A right handle on card N calls handleResizeStart(N).
   * A left  handle on card N calls handleResizeStart(N - 1).
   */
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, leftIndex: number) => {
      if (!editMode || leftIndex < 0 || leftIndex >= count - 1) return
      e.preventDefault()
      const row = rowRef.current
      if (!row) return
      const rowWidth = row.getBoundingClientRect().width

      // Snapshot all cell widths from DOM so percentages are accurate
      // (accounts for gap space, so sum of pcts stays < 100%).
      const cells = Array.from(row.children) as HTMLElement[]
      const measured: (number | null)[] = [...flexBasisPct]
      cells.forEach((cell, i) => {
        const origIdx = order[i]
        if (measured[origIdx] === null) {
          measured[origIdx] =
            (cell.getBoundingClientRect().width / rowWidth) * 100
        }
      })
      setFlexBasisPct(measured)

      const leftPct = measured[order[leftIndex]] ?? 100 / count
      const rightPct = measured[order[leftIndex + 1]] ?? 100 / count

      resizeState.current = {
        index: leftIndex,
        startX: e.clientX,
        startWidths: [leftPct, rightPct],
      }
      setResizing(leftIndex)
    },
    [editMode, count, order, flexBasisPct],
  )

  useEffect(() => {
    if (resizing === null) return
    const state = resizeState.current
    if (!state) return
    const onMove = (e: MouseEvent): void => {
      const s = resizeState.current
      const row = rowRef.current
      if (!s || !row) return
      const rowWidth = row.getBoundingClientRect().width
      const deltaPx = e.clientX - s.startX
      const deltaPct = (deltaPx / rowWidth) * 100
      const [leftPct, rightPct] = s.startWidths
      const total = leftPct + rightPct
      // Minimum 10% per card so cards stay usable
      const minPct = Math.min(10, total * 0.15)
      const newLeft = Math.max(
        minPct,
        Math.min(total - minPct, leftPct + deltaPct),
      )
      const newRight = total - newLeft
      setFlexBasisPct((prev) => {
        const next = [...prev]
        next[order[s.index]] = newLeft
        next[order[s.index + 1]] = newRight
        return next
      })
    }
    const onUp = (): void => {
      resizeState.current = null
      setResizing(null)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'
    return onUp
  }, [resizing, order])

  const getFlexStyle = useCallback(
    (orderIndex: number): React.CSSProperties => {
      const isLast = orderIndex === count - 1
      if (!editMode && isLast && lastCellFlexBasis) {
        return {
          flex: `0 0 ${lastCellFlexBasis}`,
          width: lastCellFlexBasis,
          minWidth: lastCellFlexBasis,
        }
      }
      const originalIndex = order[orderIndex]
      const pct = flexBasisPct[originalIndex]
      if (pct != null) {
        return {
          flex: `0 0 ${pct}%`,
          maxWidth: `${pct}%`,
          minWidth: 0,
        }
      }
      return { minWidth: 0 }
    },
    [order, flexBasisPct, editMode, count, lastCellFlexBasis],
  )

  return (
    <div
      ref={rowRef}
      className={`dashboard-editable-row ${editMode && multiSectionLayout ? 'dashboard-editable-row--edit' : ''} ${className}`.trim()}
    >
      {orderedChildren.map((child, orderIndex) => {
        const originalIndex = order[orderIndex]
        const isFirst = orderIndex === 0
        const isLast = orderIndex === orderedChildren.length - 1
        return (
          <div
            key={originalIndex}
            className="dashboard-editable-row-cell"
            style={getFlexStyle(orderIndex)}
            onDragOver={
              editMode && multiSectionLayout ? handleDragOver : undefined
            }
            onDrop={
              editMode && multiSectionLayout
                ? (e) => handleDrop(e, orderIndex)
                : undefined
            }
          >
            {/* Left resize handle (all cards except the first) */}
            {editMode && multiSectionLayout && !isFirst && (
              <div
                className="dashboard-editable-row-resize-handle dashboard-editable-row-resize-handle--left"
                onMouseDown={(e) => handleResizeStart(e, orderIndex - 1)}
                title="Drag to resize width"
                aria-label="Resize section width from left"
              />
            )}

            <div className="dashboard-editable-row-cell-inner">
              {editMode && multiSectionLayout && (
                <div
                  className="dashboard-editable-row-drag-handle"
                  draggable
                  onDragStart={(e) => handleDragStart(e, orderIndex)}
                  onDragEnd={handleDragEnd}
                  title="Drag to reorder"
                  aria-label="Drag to reorder section"
                >
                  <i className="mgc_move_line" aria-hidden />
                </div>
              )}
              <DashboardEditableRowSlotProvider
                multiSectionLayout={multiSectionLayout}
              >
                {child}
              </DashboardEditableRowSlotProvider>
            </div>

            {/* Right resize handle (all cards except the last) */}
            {editMode && multiSectionLayout && !isLast && (
              <div
                className="dashboard-editable-row-resize-handle dashboard-editable-row-resize-handle--right"
                onMouseDown={(e) => handleResizeStart(e, orderIndex)}
                title="Drag to resize width"
                aria-label="Resize section width from right"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default DashboardEditableRow
